import { useState } from 'react';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Typography, Box,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';
import { checkApiStatus } from '../../../utils';
import { globalDeleteService } from '../../../utils/globalApiServices';
import { deleteInvoiceApi } from '../apiService';
import { PageLoader } from '../../../shared_elements';
import { trackActivity } from '../../../utils/mixpanel';
import { canDeleteInvoiceItem, getInvoiceDeleteBlockedReason } from '..';

const BulkDeleteInvoiceModal = ({ selectedInvoices, params, pageTitle, onClose, getResponseBack }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);

    const deletableInvoices = selectedInvoices.filter(canDeleteInvoiceItem);
    const blockedInvoices = selectedInvoices.filter(inv => !canDeleteInvoiceItem(inv));

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Bulk Delete Invoice', page_type });
    };

    const onConfirmDelete = () => {
        setLoading(true);
        const idsToDelete = deletableInvoices.map(inv => inv.id);
        globalDeleteService(deleteInvoiceApi(params), { ids: idsToDelete })
            .then(response => {
                setLoading(false);
                if (checkApiStatus(response)) {
                    trackActivity('Bulk Invoice Deleted', {
                        page_title: pageTitle,
                        total: idsToDelete.length,
                        success_msg: response?.data?.message,
                        item_ids: idsToDelete,
                        response: response?.data,
                    });
                    enqueueSnackbar(response?.data?.message || `${idsToDelete.length} invoice(s) deleted successfully`, {
                        variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    });
                    getResponseBack();
                    onClose();
                } else {
                    trackActivity('Bulk Invoice Deleted', {
                        page_title: pageTitle,
                        total: idsToDelete.length,
                        error_msg: response?.data?.message,
                        item_ids: idsToDelete,
                        response: response?.data,
                    });
                    enqueueSnackbar(response?.data?.message || 'Failed to delete the selected invoice(s)', {
                        variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    });
                }
            });
    };

    return (
        <>
            <Dialog open maxWidth="xs" fullWidth aria-labelledby="bulk-delete-dialog-title" className="add-lease-dialog">
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className="dialog-header" id="bulk-delete-dialog-title">
                    Delete Invoices
                    <Box sx={{ bgcolor: '#f1f5f9', color: '#475569', px: 1.2, py: 0.3, borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                        {selectedInvoices.length} Invoice{selectedInvoices.length > 1 ? 's' : ''}
                    </Box>
                </DialogTitle>
                <DialogContent dividers style={{ minWidth: '400px' }}>
                    <Typography sx={{ fontSize: 14, mb: blockedInvoices.length > 0 ? 2 : 0 }}>
                        {deletableInvoices.length > 0
                            ? `Are you sure you want to delete ${deletableInvoices.length} invoice${deletableInvoices.length > 1 ? 's' : ''} ?`
                            : 'None of the selected invoices can be deleted.'}
                    </Typography>

                    {blockedInvoices.length > 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px' }}>
                                    Cannot be deleted ({blockedInvoices.length})
                                </Typography>
                                <Tooltip title="These invoices will be skipped and left as-is." placement="right" arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: '15px', color: '#64748b', cursor: 'pointer' }} />
                                </Tooltip>
                            </Box>
                            <TableContainer component={Paper} className="lease-detail-table" sx={{ maxHeight: 185, overflowY: 'auto' }}>
                                <Table size="small" stickyHeader className="mui-table-format">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Invoice Number</TableCell>
                                            <TableCell>Reason</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {blockedInvoices.map((inv, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{inv.invoice_number || '--'}</TableCell>
                                                <TableCell>{getInvoiceDeleteBlockedReason(inv)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ paddingRight: '16px' }}>
                    <Button className="cancel-btn" variant="outlined" size="small" disabled={isLoading} onClick={() => { onClose(); handleItemClicked('Cancel Button') }}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" size="small" disabled={isLoading || deletableInvoices.length === 0} onClick={() => { onConfirmDelete(); handleItemClicked('Delete Button') }}>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : `Delete${deletableInvoices.length > 0 ? ` (${deletableInvoices.length})` : ''}`}
                    </Button>
                </DialogActions>
            </Dialog>
            {isLoading && <PageLoader />}
        </>
    );
};

export default BulkDeleteInvoiceModal;
